import { useEffect, useState } from 'react';
import { MAX_DAYS, StandardUnits, useFirestore } from './useFirestore';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import _ from 'lodash';

dayjs.extend(isSameOrBefore);
dayjs.extend(weekOfYear);

export const useConsumption = () => {
  const [nowDate, setNowDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [todayByUnit, setTodayByUnit] = useState([]);
  const [total, setTotal] = useState({ today: 0, week: 0, daily: {} });
  const {
    addConsumption,
    subscribeConsumption,
    updateConsumptionConfirmation,
    consumption,
    fireStoreReady,
  } = useFirestore();

  useEffect(() => {
    //console.log('--->>> useConsumption#useEffect[consumption]', consumption);
    if (consumption) {
      const now = dayjs(nowDate);
      const totalByUnitToday = totalByUnitForDay(consumption, now);
      const totalByUnitWeek = totalByUnitForNumDays(7);

      const totalToday = totalByUnitToday.reduce((sum, o) => sum + o.total, 0);
      let totalForWeek = 0;
      totalByUnitWeek.forEach((dbu) => {
        totalForWeek += dbu.reduce((sum, o) => sum + o.total, 0);
      });

      const daily = amountByDay();
      const allDates = Object.keys(daily);
      const days = allDates?.length;

      const zeroDates = allDates.filter((date) => daily[date] === 0); // Array of zero dates
      const confirmed = consumption // Array of confirmed dates (usually zero)
        .filter((item) => item.confirmed)
        .map((item) => item.datetime.split('T')[0]);
      const zero = zeroDates.filter((date) => confirmed.includes(date))?.length;

      // Forgotten
      const forgotten = consumption.filter(
        (c) =>
          c.unitId === 0 &&
          !c.confirmed &&
          zeroDates.includes(c.datetime.substring(0, 10))
      )?.length;

      const units = amountByUnitId();

      setTodayByUnit(totalByUnitToday);
      setTotal({
        today: totalToday,
        week: totalForWeek,
        daily,
        weekly: amountByWeek(),
        averageByWeekday: averageByWeekday(),
        zero,
        forgotten,
        days,
        units,
      });
    }
  }, [consumption]);

  const confirmZero = async (consumptionId) => {
    await updateConsumptionConfirmation(consumptionId, true);
  };

  const insertEmptyDays = async ({ from, to }) => {
    if (!from || !to) {
      return;
    }
    const registeredAt = dayjs().format('YYYY-MM-DDTHH:mm:ss');
    const promises = [];
    while (from.isSameOrBefore(to)) {
      promises.push(
        addConsumption({
          amount: 0,
          confirmed: false,
          datetime: from.format('YYYY-MM-DDTHH:mm:ss'),
          location: null,
          registeredAt,
          unitId: 0,
        })
      );
      from = from.add(1, 'days');
    }
    await Promise.all(promises);
  };

  // [
  //   { unitId: 'A', total: 15 },
  //   { unitId: 'B', total: 7 },
  // ]
  const totalByUnitForDay = (consumption, day) => {
    const allForDay = consumption?.filter((c) =>
      dayjs(c.datetime).isSame(day, 'day')
    );
    const totalByUnit = StandardUnits.map((u) => ({
      unitId: u.unitId,
      total: 0,
    }));
    allForDay?.forEach((u) => {
      const unit = totalByUnit.find((t) => t.unitId === u.unitId);
      unit.total += u.amount;
    });
    return totalByUnit;
  };

  const totalByUnitNameForDay = (consumption, day) => {
    const byUnitForDay = totalByUnitForDay(consumption, day);
    return {
      date: day.format('YYYY-MM-DD'),
      beerII: byUnitForDay[1].total,
      beerIII: byUnitForDay[2].total,
      wine: byUnitForDay[3].total,
      spirit: byUnitForDay[4].total,
      total:
        byUnitForDay[1].total +
        byUnitForDay[2].total +
        byUnitForDay[3].total +
        byUnitForDay[4].total,
    };
  };

  const totalByUnitForNumDays = (numDays) => {
    const num = Math.min(numDays, MAX_DAYS);
    const result = [];
    let date = dayjs(nowDate);
    for (let i = 0; i < num; i++) {
      result.push(totalByUnitForDay(consumption, date));
      date = date.subtract(1, 'day');
    }
    return result;
  };

  // Returns
  // {
  //   '2023-07-12': 2,
  //   '2023-07-13': 4
  // }
  const amountByDay = () => {
    return consumption.reduce((acc, current) => {
      const date = current.datetime.split('T')[0];
      acc[date] = acc[date] ? acc[date] + current.amount : current.amount;
      return acc;
    }, {});
  };

  // Returns
  // {
  //   '23': 2,
  //   '24': 4
  // }
  const amountByWeek = () => {
    const weeklySums = {};
    consumption.forEach((item) => {
      const weekNumber = dayjs(item.datetime.split('T')[0]).week();
      if (!weeklySums[weekNumber]) {
        weeklySums[weekNumber] = 0;
      }
      weeklySums[weekNumber] += item.amount;
    });

    //console.log('---> weeklySums', weeklySums, dayjs('2023-09-10').week());

    return weeklySums;
  };

  // Returns
  // {
  //   '1': 21,
  //   '2': 41,
  //   '3': 21,
  //   '4': 41
  // }
  const amountByUnitId = () => {
    const unitSums = {};

    consumption.forEach((item) => {
      if (item.unitId !== 0) {
        if (!unitSums[item.unitId]) {
          unitSums[item.unitId] = 0;
        }

        unitSums[item.unitId] += item.amount;
      }
    });

    return unitSums;
  };

  // Example output:
  // {
  //   0: 5, // Average for Sunday
  //   1: 3, // Average for Monday
  //   2: 4, // Average for Tuesday
  //   ...
  // }
  const averageByWeekday = () => {
    const weekdaySums = {};
    const uniqueDatesPerWeekday = {};

    consumption.forEach((item) => {
      const date = item.datetime.split('T')[0];
      const weekday = dayjs(date).day(); // 0 (Sunday) to 6 (Saturday)

      if (!weekdaySums[weekday]) {
        weekdaySums[weekday] = 0;
        uniqueDatesPerWeekday[weekday] = new Set();
      }

      // Only proceed if the consumption amount is greater than zero
      if (item.amount) {
        weekdaySums[weekday] += item.amount;

        // Add date to the set for this weekday, only if consumption is non-zero
        uniqueDatesPerWeekday[weekday].add(date);
      }
    });

    const averageWeekdayAmount = {};
    Object.keys(weekdaySums).forEach((weekday) => {
      // If there were no days with consumption, set average to 0
      if (uniqueDatesPerWeekday[weekday].size === 0) {
        averageWeekdayAmount[weekday] = 0;
      } else {
        const average =
          weekdaySums[weekday] / uniqueDatesPerWeekday[weekday].size;
        averageWeekdayAmount[weekday] = Math.round(average * 10) / 10;
      }
    });

    return averageWeekdayAmount;
  };

  // Example of usage:
  // const consumption = [
  //   { datetime: '2024-01-13T08:00:00', amount: 1 },
  //   { datetime: '2024-01-13T09:00:00', amount: 1 },
  //   // ... more data
  // ];
  // const result = amountByWeekday(consumption);
  // console.log(result);
  // This might output something like:
  // {
  //   '0': 4.0,  // Sunday
  //   '1': 5.2,  // Monday
  //   // ... other weekdays
  // }
  const totalByWeekday = () => {
    const weekdaySums = {};

    consumption.forEach((item) => {
      const weekdayNumber = dayjs(item.datetime.split('T')[0]).day();
      if (!weekdaySums[weekdayNumber]) {
        weekdaySums[weekdayNumber] = 0;
      }
      weekdaySums[weekdayNumber] += item.amount;
    });

    // Rounding the results to one decimal place
    for (const day in weekdaySums) {
      weekdaySums[day] = Math.round(weekdaySums[day] * 10) / 10;
    }

    return weekdaySums;
  };

  const refreshNowDate = () => setNowDate(dayjs().format('YYYY-MM-DD'));

  return {
    addConsumption,
    confirmZero,
    consumption,
    fireStoreReady,
    insertEmptyDays,
    refreshNowDate,
    subscribeConsumption,
    todayByUnit,
    total,
    totalByUnitForDay,
  };
};
